import React, { useEffect, useState, useCallback } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProfileFieldset, { buildProfilePayload } from "@/components/interest/ProfileFieldset";
import { NeedsLoginPanel, DuplicatePrompt, SuccessScreen } from "@/components/interest/InterestShared";
import { UserPlus } from "lucide-react";

export default function CreateProfile() {
  const { user } = useAuth();
  const [f, setF] = useState({});
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(null);
  const [dup, setDup] = useState(null);
  const [success, setSuccess] = useState(null);

  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);

  useEffect(() => {
    if (!user) return;
    api.get("/players/me").then(({ data }) => {
      const p = data?.player || data;
      if (!p) return;
      setF((prev) => ({
        ...prev, first_name: p.first_name || "", last_name: p.last_name || "", email: p.email || user.email || "",
        phone: p.phone || "", date_of_birth: p.date_of_birth || "", graduation_year: p.graduation_year || "",
        city: p.city || "", province: p.province || "", primary_position: p.primary_position || "",
        secondary_position: p.secondary_position || "", bats: p.bats || "", throws: p.throws || "",
        current_team: p.current_team || "", league: p.league || "", school: p.school || "",
        parent_name: p.parent_name || "", parent_email: p.parent_email || "", parent_phone: p.parent_phone || "",
        instagram_handle: p.instagram_handle || "", x_handle: p.x_handle || "", profile_photo_url: p.profile_photo_url || "",
      }));
    }).catch(() => {});
  }, [user]);

  const submit = async (extra = {}) => {
    setError("");
    const required = { first_name: "First name", last_name: "Last name", email: "Player email",
      date_of_birth: "Date of birth", graduation_year: "Graduation year", province: "Province",
      primary_position: "Primary position" };
    const missing = Object.entries(required).filter(([k]) => !String(f[k] || "").trim()).map(([, l]) => l);
    if (missing.length) { setError(`Please complete: ${missing.join(", ")}`); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (!consent) { setError("Please acknowledge the terms to create your profile."); return; }
    setBusy(true);
    try {
      const { profile, media } = buildProfilePayload(f);
      const { data } = await api.post("/interests/create-profile", { profile, media, consent: true, source: "create_free_profile", ...extra });
      if (data.needs_login) { setNeedsLogin(data.message); setDup(null); return; }
      if (data.possible_duplicate) { setDup(data); return; }
      if (data.ok) {
        setSuccess({ claimRequired: data.claim_required,
          message: "Your free CBR Prospects profile is ready. Track your development, receive invitations to CBR Nation opportunities, and showcase your baseball journey." });
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#E63946] bg-white/5 px-3 py-1.5 rounded-sm">
            <UserPlus className="w-3.5 h-3.5" /> Free Profile
          </div>
          <h1 data-testid="create-profile-title" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-4">Create Your Free CBR Prospects Profile</h1>
          <p className="text-white/70 text-sm mt-3 max-w-2xl leading-relaxed">
            Build your baseball profile, track your development, receive invitations to CBR Nation opportunities, and showcase your baseball journey.
          </p>
        </div>
      </section>

      <section className="bg-[#F1F3F6] py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-10">
            {success ? (
              <SuccessScreen title="Profile Created!" message={success.message} claimRequired={success.claimRequired} />
            ) : needsLogin ? (
              <NeedsLoginPanel message={needsLogin} redirectTo="/create-profile" />
            ) : dup ? (
              <DuplicatePrompt candidates={dup.candidates} message={dup.message} busy={busy}
                onPickExisting={(id) => submit({ confirm_existing_player_id: id })}
                onCreateNew={() => submit({ acknowledge_new: true })} />
            ) : (
              <>
                {error && <div data-testid="create-profile-error" className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946] mb-6">{error}</div>}
                <ProfileFieldset f={f} set={set} testidPrefix="cp" />
                <label className="mt-8 flex items-start gap-3 cursor-pointer">
                  <input data-testid="create-profile-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                  <span className="text-sm text-slate-700">
                    I consent to CBR Prospects creating a player profile with the information provided and contacting me about CBR opportunities.
                  </span>
                </label>
                <button data-testid="create-profile-submit" onClick={() => submit()} disabled={busy}
                  className="mt-8 w-full bg-[#E63946] hover:bg-[#D90429] text-white py-4 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 transition-colors">
                  {busy ? "Creating…" : "Create Free Profile"}
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
